// Navigator.js
// =============================================================================
// A floating "where am I, and how do I…" panel.
// =============================================================================
//
// Opened by "Show navigator" at the foot of the left rail. Movable by its
// header, closable by the X, and always on top. It stays open while you
// navigate — that is the point: you can read the answer and follow it at the
// same time, which a modal dialog makes impossible.
//
// Three sections:
//   1. WHERE YOU ARE — driven by currentView. Updates as you move.
//   2. ASK — a question box.
//   3. ANSWER — the path to the thing, the steps, and a button that takes you
//      there where the destination is a top-level route.
//
// WHY THIS IS A KNOWLEDGE BASE AND NOT AN LLM CALL
// ------------------------------------------------
// A navigator's whole value is being RIGHT about where things are. An answer
// that invents a plausible-sounding menu path ("Settings → Staff → Schedules")
// is worse than no answer, because the user goes looking and concludes the app
// is broken rather than the help. Every path below is taken from the source, so
// it is either correct or a bug someone can fix.
//
// The cost is coverage: it only knows what is in HOW_TO. So when nothing
// matches, it says so plainly and offers the topics it does hold, rather than
// generating something confident and wrong. If this is later backed by a model,
// keep that division — let the model REPHRASE these entries, not invent paths.
//
// KEEPING IT TRUE
// ---------------
// These entries describe the UI. When the UI moves, they go stale silently —
// nothing will fail a build. The `where` line of any entry you touch during a
// change is worth updating in the same commit.
// =============================================================================

import React, { useEffect, useMemo, useRef, useState } from "react";

/* ========================================================================== */
/* 1. WHERE YOU ARE — one entry per top-level route                           */
/* ========================================================================== */
const CONTEXT = {
  head_office: {
    title: "Head Office",
    blurb: "The live fleet dashboard — current status across every branch at a glance.",
    can: [
      "See where each branch stands right now on the things that need attention",
      "Open Staff for clocking in/out, schedules, the workforce planner and the pharmacist rota",
      "Work through MHRA safety alerts, near misses and the accident book"
    ],
    related: ["staff-schedule", "clock-review", "mhra", "near-miss"]
  },
  mockup: {
    title: "Site Activity",
    blurb: "Aggregated activity across all sites between two dates you choose.",
    can: ["Pick a date range and see the totals across the fleet"],
    related: ["business-insights"]
  },
  single_pharmacy: {
    title: "Single pharmacy",
    blurb: "The Head Office dashboard scoped to one branch at a time.",
    can: ["See the live status of one pharmacy without the rest of the fleet in the way"],
    related: []
  },
  regulatory_compliance: {
    title: "Regulatory Compliance",
    blurb: "Fleet compliance against GPhC standards and statutory duties, tabbed by area.",
    can: [
      "Drill from a compliance area down to per-branch and per-item completion",
      "Record alternative evidence where a branch keeps paper records"
    ],
    related: ["compliance-attestation"]
  },
  communications: {
    title: "Communications",
    blurb: "Every message the platform sends, gathered in one place — and the Action centre, which lives here rather than in Head Office.",
    can: [
      "Send and track fleet-wide messages that need acknowledging (Action centre)",
      "See everything sent to one member of staff, and send them a new message (Messages)",
      "Find where every other kind of message is configured or sent from"
    ],
    related: ["staff-messages-view", "staff-messages-send", "action-centre", "staff-notifications"]
  },
  business_insights: {
    title: "Business Insights",
    blurb: "Monthly income, cost and contribution per branch, with fleet comparisons and trends.",
    can: [
      "Compare a branch against the rest of the fleet",
      "Follow the trend month by month and read the recommendations"
    ],
    related: ["configurables"]
  },
  dashpanels: {
    title: "Custom Live monitoring panels",
    blurb: "Monitoring panels built from a plain-English description, refreshed on an interval you choose.",
    can: [
      "Describe a panel in ordinary words and have it built",
      "Arrange panels and save the arrangement as a named dashboard"
    ],
    related: []
  },
  route_analysis: {
    title: "Delivery → Route analysis",
    blurb: "Delivery driver routes and how efficiently they run.",
    can: ["Look at a round and see where the time and mileage go"],
    related: []
  },
  delivery_activity: {
    title: "Delivery → Activity",
    blurb: "Deliveries, mileage and time over a date range, by site and driver.",
    can: ["Compare drivers and sites over a period"],
    related: []
  },
  stock_financials: {
    title: "Stock → Scanned items",
    blurb: "The value of stock counted onto the shelf, by branch.",
    can: [
      "See where capital is tied up across the fleet",
      "Switch to the pictorial shelf layout from the button in its header"
    ],
    related: ["stock_excess", "warehouse_view"]
  },
  shelfPictorialView: {
    title: "Stock → Pictorial",
    blurb: "A shelf-by-shelf pictorial view of stock. Reached from Scanned items, not from the menu.",
    can: ["See what is where, laid out as the shelves are"],
    related: ["stock_financials"]
  },
  stock_excess: {
    title: "Stock → Analysis and transfer (detailed)",
    blurb: "Line by line, what each branch holds against what it needs. Reached from the Detailed button on the overview, not from the menu.",
    can: ["Find stock to rebalance between branches or return"],
    related: ["stock_financials", "warehouse_view"]
  },
  warehouse_view: {
    title: "Stock → Analysis and transfer",
    blurb: "What can move branch to branch, and what can come back to the warehouse. Where the menu entry lands; the Detailed button goes a level down.",
    can: [
      "See how many months each branch's stock will last",
      "Raise every worthwhile branch-to-branch transfer in one go",
      "Find spare stock no branch needs, ready for warehouse retrieval"
    ],
    related: ["stock_excess", "stock_financials"]
  },
  services: {
    title: "Services / Appointments analysis",
    blurb: "Uptake and activity for commissioned services.",
    can: ["Compare service delivery across branches"],
    related: []
  },
  nms: {
    title: "New Medicine Service",
    blurb: "NMS activity and completion across branches.",
    can: ["See which branches are completing NMS and which are not"],
    related: []
  },
  epos: {
    title: "EPOS",
    blurb: "Till and shop transaction analysis.",
    can: ["Look at shop takings and transactions"],
    related: []
  },
  site_management: {
    title: "Admin → Site management",
    blurb: "Every organisation on the platform — members, login details and access emails.",
    can: ["Administer organisations and their members"],
    related: []
  },
  sitedetails: {
    title: "Admin → Site details",
    blurb: "Per-site configuration and metadata.",
    can: ["Check and adjust how an individual site is set up"],
    related: []
  },
  smsprod: {
    title: "Admin → SMS history",
    blurb: "Messages sent to patients.",
    can: ["Look back at what was sent and when"],
    related: []
  },
  config: {
    title: "Configurables",
    blurb: "Admin-editable values used across the platform, including the financial assumptions behind Business Insights.",
    can: ["Change a value once and have every calculation that uses it follow"],
    related: ["business-insights"]
  },
  qmap: {
    title: "Admin → Map",
    blurb: "A map view of branches and related locations.",
    can: ["See the estate geographically"],
    related: []
  },
  pickups: {
    title: "Admin → Collection times",
    blurb: "When prescriptions are being collected across branches.",
    can: ["See the collection pattern across the day"],
    related: []
  }
};

const FALLBACK_CONTEXT = {
  title: "Pharmace management",
  blurb: "Pick a section from the menu on the left. Head Office is the usual starting point — the live status of the whole fleet.",
  can: [],
  related: ["staff-schedule", "staff-messages-view", "business-insights"]
};

/* ========================================================================== */
/* 2. HOW DO I… — the answerable questions                                    */
/* ========================================================================== */
// `where` is the click path, as it actually is in the app.
// `view` is a top-level route, so the panel can offer to take you there. Leave
//        it out for anything reached through a dialog or a tab — offering to
//        navigate somewhere the button cannot actually reach is worse than not
//        offering.
const HOW_TO = [
  {
    id: "staff-schedule",
    title: "Create or change a staff member's schedule",
    keywords: ["schedule", "schedules", "rota", "hours", "contracted", "shift", "shifts",
               "working pattern", "pattern", "four week", "create schedule", "staff hours"],
    where: "Head Office → Staff → Clock in/out",
    view: "head_office",
    steps: [
      "Open Head Office, then Staff, then the Clock in/out section.",
      "In Month view, press \"Configure scheduled hours\" — or in Day view, click the person's name.",
      "Both open the same four-week editor: weeks 1 to 4 are set separately, because patterns often differ week to week while every month repeats.",
      "Fill in week 1, then press \"Replicate week 1 → weeks 2-4\" and adjust the days that differ.",
      "Press \"Save all changes\". Nothing is written until you do — per-row Apply buttons were removed."
    ],
    note: "A 5th occurrence of a weekday in a longer month follows the week 1 pattern."
  },
  {
    id: "schedule-invite",
    title: "Ask a staff member to confirm their own schedule",
    keywords: ["invite", "self service", "confirm schedule", "email schedule", "schedule form",
               "staff confirm", "send schedule", "link"],
    where: "Head Office → Staff → Clock in/out → the schedule editor",
    view: "head_office",
    steps: [
      "Open the schedule editor for that person (see \"Create or change a staff member's schedule\").",
      "Press \"Email schedule form\".",
      "They receive a personal link to a form covering weeks 1 to 4, which they fill in themselves."
    ],
    note: "The link expires after 24 hours, and it goes to one named person at a time — the server refuses bulk sending of these on purpose, because each link is a credential. Whether it sent, failed, or was skipped for want of an email address is recorded in Communications → Messages."
  },
  {
    id: "clock-review",
    title: "Review a clocking event, or approve overtime",
    keywords: ["clock", "clocking", "clock in", "clock out", "overtime", "approve", "review",
               "attendance", "late", "timesheet review", "payroll hours"],
    where: "Head Office → Staff → Clock in/out",
    view: "head_office",
    steps: [
      "Open Head Office → Staff → Clock in/out.",
      "In Day view, click the cell for that person and day.",
      "The dialog shows the planned session, what actually happened, and the analysis.",
      "Approve overtime or tick \"Review complete\" there. A reviewed cell turns blue so handled exceptions stop standing out as problems."
    ],
    note: "Cell colours: green is clean and complete, amber is still clocked in, red needs attention, blue has been reviewed."
  },
  {
    id: "timesheet-export",
    title: "Export timesheets for payroll",
    keywords: ["timesheet", "timesheets", "payroll", "export", "excel", "spreadsheet",
               "submit payroll", "monthly hours"],
    where: "Head Office → Staff → Clock in/out → Month view",
    view: "head_office",
    steps: [
      "Open Head Office → Staff → Clock in/out and switch to Month view.",
      "Export from there. The sheet is grouped under banner blocks: PAYROLL (the figures to submit, highlighted, with a column explaining how each was calculated), THE PLAN, WHAT HAPPENED, and REVIEW.",
      "The totals row carries a \"submit to payroll\" sentence and a count of how many days were reviewed."
    ],
    note: "Timesheets can also be emailed to the group manager automatically each month — see \"Have reports emailed automatically\"."
  },
  {
    id: "staff-messages-view",
    title: "See what has been sent to a member of staff",
    keywords: ["sent", "history", "messages", "message history", "what did we send",
               "did they get", "email history", "communications", "audit"],
    where: "Communications → Messages",
    view: "communications",
    steps: [
      "Open Communications from the left menu, then Messages.",
      "Pick the person from the list on the left — it shows name, role and branch.",
      "Their history appears on the right, newest first, with how each one was sent (email or push notification) and whether it arrived."
    ],
    note: "History starts from when this was switched on. Anything sent before then was recorded in several separate places with no common format, so it is not shown."
  },
  {
    id: "staff-messages-send",
    title: "Send a message to staff",
    keywords: ["send message", "message staff", "email staff", "tell staff", "announce",
               "notify staff", "bulk email", "send email"],
    where: "Communications → Messages → Send message",
    view: "communications",
    steps: [
      "Open Communications → Messages and press \"Send message\".",
      "Tick who it goes to on the left. Role filter buttons start with every role on; there is a select / deselect all for whatever is currently shown.",
      "Write the subject and message on the right.",
      "Press Continue. Before anything is sent you get a count, a breakdown by role and branch, who has no email address on record, and the message exactly as it will arrive."
    ],
    note: "Messages go to each person individually — recipients cannot see one another. Push notifications are not available yet, so these go by email."
  },
  {
    id: "action-centre",
    title: "Send a message to a pharmacy and track that it was read",
    keywords: ["action centre", "action center", "acknowledge", "acknowledgement", "read receipt",
               "message pharmacy", "fleet message", "branch message"],
    where: "Communications → Action centre",
    view: "communications",
    steps: [
      "Open Communications. The Action centre is the first section.",
      "Compose a message to one pharmacy or the whole fleet, and tick whether it must be acknowledged.",
      "Messages awaiting acknowledgement sit in the top grid; acknowledged ones move down."
    ],
    note: "This moved out of Head Office and now lives only in Communications."
  },
  {
    id: "staff-notifications",
    title: "Set up automatic reminders about clocking in and out",
    keywords: ["notification", "notifications", "push", "reminder", "reminders", "alert",
               "break ending", "not arrived", "pharmacist not arrived", "automatic"],
    where: "Settings → Communications",
    steps: [
      "Open Settings from the left menu and go to the Communications tab.",
      "Four reminders can be switched on: break ending in five minutes, clock-out due, clock-in missing, and a manager alert that the pharmacist has not arrived.",
      "Each one's wording is editable, with placeholders replaced when it sends."
    ],
    note: "NOT LIVE. Settings save and are kept, but nothing is delivered yet — the staff app does not receive push notifications. Do not rely on these to tell you a pharmacist has not arrived."
  },
  {
    id: "appraisals",
    title: "Run a staff appraisal",
    keywords: ["appraisal", "appraisals", "review", "annual review", "pre appraisal",
               "appraiser", "objectives", "one to one"],
    where: "Head Office → Staff → appraisals",
    view: "head_office",
    steps: [
      "Open Head Office → Staff and go to the appraisals section.",
      "Send the pre-appraisal questionnaire to the staff member — their answers form the agenda for the meeting.",
      "Send the appraiser their form, which records the whole appraisal from one device.",
      "Afterwards, the summary email can be edited before sending; you see exactly what will go."
    ],
    note: "Sending the summary again replaces the stored snapshot."
  },
  {
    id: "locum-rota",
    title: "Cover a session, or circulate free sessions to locums",
    keywords: ["locum", "locums", "pharmacist rota", "cover", "session", "sessions",
               "circulate", "booking", "book pharmacist", "free sessions"],
    where: "Head Office → Staff → Clock in/out → Pharmacist rota",
    view: "head_office",
    steps: [
      "Open Head Office → Staff → Clock in/out and go to the Pharmacist rota tab.",
      "Circulate free sessions — each locum on the register gets a personalised email showing only their own free sessions.",
      "Booking a session sends a confirmation to the pharmacist at the address in the register.",
      "Circulation history shows every previous circulation, who it went to, and the exact email each one received."
    ],
    note: "The monthly locum payment schedule goes to the group manager automatically on the 1st, if switched on."
  },
  {
    id: "reports-scheduled",
    title: "Have reports emailed automatically",
    keywords: ["report", "reports", "scheduled", "automatic report", "monthly", "email report",
               "fridge", "expiring", "slow movers", "sim", "expired drugs", "timesheets monthly"],
    where: "Settings → Organisation",
    steps: [
      "Open Settings and go to the Organisation tab.",
      "Switch on the reports you want the group manager to receive: SIM activity, expiring items, slow movers, fridge checks, removed/expired drugs, and monthly timesheets.",
      "Each has its own schedule setting."
    ],
    note: "The monthly site performance review and the locum payment schedule are configured on their own pages rather than here."
  },
  {
    id: "mhra",
    title: "Deal with an MHRA safety alert",
    keywords: ["mhra", "safety alert", "alert", "recall", "drug alert", "medicines recall"],
    where: "Head Office → MHRA alerts",
    view: "head_office",
    steps: [
      "Open Head Office and go to MHRA alerts.",
      "Push a live alert out to the fleet.",
      "Track which sites have actioned it, with each site's action detail — date, whether they were affected, stock details, and who it was cascaded to."
    ],
    note: "The alert list is searchable across title, summary and the full article text. Words are ANDed by default; OR separates alternatives."
  },
  {
    id: "near-miss",
    title: "Look at near misses",
    keywords: ["near miss", "near misses", "error", "errors", "incident", "dispensing error",
               "patterns"],
    where: "Head Office → near miss",
    view: "head_office",
    steps: [
      "Open Head Office and go to the near miss section.",
      "Patterns can be raised to the affected pharmacy.",
      "Near-miss alerts also seed the suggested messages in the Action centre."
    ]
  },
  {
    id: "accident-riddor",
    title: "Record an accident, or chase a RIDDOR decision",
    keywords: ["accident", "accident book", "riddor", "injury", "reportable", "health and safety"],
    where: "Head Office → accident book",
    view: "head_office",
    steps: [
      "Open Head Office and go to the accident book.",
      "Accidents whose RIDDOR status is still unknown can be chased.",
      "Each row records the date of the last reminder."
    ]
  },
  {
    id: "compliance-attestation",
    title: "Record paper evidence of compliance",
    keywords: ["attestation", "paper", "evidence", "alternative evidence", "compliance",
               "manual record", "not digital"],
    where: "Regulatory Compliance",
    view: "regulatory_compliance",
    steps: [
      "Open Regulatory Compliance and find the compliance element.",
      "Where there is no digital data stream, record the alternative evidence — for instance that the branch keeps a paper record.",
      "One active attestation is held per branch per element."
    ]
  },
  {
    id: "business-insights",
    title: "Compare a branch's financial performance",
    keywords: ["business insights", "income", "cost", "costs", "contribution", "profit",
               "financial", "compare branches", "benchmark", "performance"],
    where: "Business Insights",
    view: "business_insights",
    steps: [
      "Open Business Insights from the left menu.",
      "Monthly income, cost and contribution are shown per branch, with fleet comparisons, trends and recommendations."
    ],
    note: "The financial assumptions behind these figures are set in Settings → Configurables."
  },
  {
    id: "configurables",
    title: "Change a financial assumption or platform-wide value",
    keywords: ["configurable", "configurables", "assumption", "assumptions", "settings",
               "value", "rate", "change setting"],
    where: "Settings → Configurables",
    steps: [
      "Open Settings and go to the Configurables tab.",
      "Change the value once; everything that uses it follows."
    ]
  },
  {
    id: "dashboards",
    title: "Build a live monitoring panel",
    keywords: ["dashboard", "dashboards", "panel", "panels", "monitor", "monitoring", "live",
               "custom", "build panel"],
    where: "Custom Live monitoring panels",
    view: "dashpanels",
    steps: [
      "Open Custom Live monitoring panels from the left menu.",
      "Describe the panel you want in ordinary words.",
      "Set the refresh interval, arrange the panels, and save the arrangement as a named dashboard."
    ]
  },
  {
    id: "workforce",
    title: "Look at staffing levels across branches",
    keywords: ["workforce", "planner", "staffing", "headcount", "hours by role", "capacity",
               "staffing levels"],
    where: "Head Office → Staff → Clock in/out → Workforce",
    view: "head_office",
    steps: [
      "Open Head Office → Staff → Clock in/out and go to the Workforce tab.",
      "Contracted hours are totalled per branch and role, alongside an item count so branches can be compared fairly by size."
    ]
  },
  {
    id: "site-management",
    title: "Administer organisations and their members",
    keywords: ["site management", "organisation", "organisations", "members", "add site",
               "login details", "access", "tenant"],
    where: "Admin → Site management",
    view: "site_management",
    steps: [
      "Open Admin → Site management from the left menu.",
      "Every organisation on the platform is listed with its members, login details and access emails."
    ],
    note: "Only visible when signed in as Pharmace all."
  }
];

/* ========================================================================== */
/* 3. Matching                                                                */
/* ========================================================================== */
// Deliberately simple and inspectable: score by how many of the question's
// meaningful words appear in an entry's keywords or title. No cleverness that
// would make a wrong answer hard to explain.
const STOP = new Set([
  "how", "do", "i", "a", "an", "the", "to", "for", "of", "in", "on", "at", "is",
  "can", "you", "me", "my", "we", "our", "what", "where", "when", "which", "and",
  "or", "with", "it", "this", "that", "there", "get", "find", "please", "help",
  "want", "need", "does", "did", "am", "are", "be", "up", "out", "new", "set"
]);

const words = (s) =>
  String(s || "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(Boolean);

function search(query) {
  const qs = words(query).filter(w => !STOP.has(w) && w.length > 1);
  if (!qs.length) return [];

  const scored = HOW_TO.map(entry => {
    const hay = [entry.title, ...(entry.keywords || []), entry.where].join(" ").toLowerCase();
    let score = 0;
    for (const w of qs) {
      // A keyword phrase matching exactly is worth more than an incidental
      // word appearing somewhere in the blob.
      if ((entry.keywords || []).some(k => k === w)) score += 3;
      else if ((entry.keywords || []).some(k => k.includes(w))) score += 2;
      else if (hay.includes(w)) score += 1;
    }
    // Title words carry extra weight — "schedule" in a title means more than
    // "schedule" mentioned in passing.
    for (const w of qs) if (words(entry.title).includes(w)) score += 2;
    return { entry, score };
  }).filter(r => r.score > 0).sort((a, b) => b.score - a.score);

  // Require a real signal, not one incidental word.
  return scored.filter(r => r.score >= 2).slice(0, 3).map(r => r.entry);
}

/* ========================================================================== */
/* Styles                                                                     */
/* ========================================================================== */
const P = {
  panel: (x, y, w, h) => ({
    position: "fixed", left: x, top: y, width: w, height: h,
    background: "#fff", border: "1px solid #cbd5e1", borderRadius: 10,
    boxShadow: "0 12px 36px rgba(15,23,42,0.25)",
    // Above the Settings and Shelves dialogs (z-index 1000) and the send
    // dialogue (9000) — "always on top" has to mean on top of those too, or it
    // disappears exactly when someone opens the thing they need help with.
    zIndex: 12000,
    display: "flex", flexDirection: "column", overflow: "hidden",
    fontFamily: "inherit"
  }),
  head: { display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "9px 12px", background: "#0f766e", color: "#fff",
          cursor: "move", userSelect: "none", flexShrink: 0 },
  body: { flex: 1, overflowY: "auto", padding: "12px 14px" },
  foot: { borderTop: "1px solid #e5e7eb", padding: "10px 12px", background: "#f8fafc", flexShrink: 0 },
  label: { fontSize: 10.5, fontWeight: 700, color: "#6b7280", textTransform: "uppercase",
           letterSpacing: 0.5, marginBottom: 5 },
  card: { border: "1px solid #e5e7eb", borderRadius: 7, padding: "10px 12px", marginBottom: 10 },
  input: { width: "100%", boxSizing: "border-box", fontSize: 13, padding: "7px 9px",
           border: "1px solid #d1d5db", borderRadius: 5, fontFamily: "inherit" },
  go: { border: "1px solid #0f766e", background: "#f0fdfa", color: "#0f766e", borderRadius: 5,
        padding: "4px 11px", fontSize: 12, fontWeight: 700, cursor: "pointer",
        fontFamily: "inherit", marginTop: 8 },
  link: { border: "none", background: "none", color: "#0f766e", fontSize: 12.5,
          fontWeight: 600, cursor: "pointer", padding: "3px 0", textAlign: "left",
          fontFamily: "inherit", display: "block" },
  where: { fontSize: 12, color: "#0f766e", background: "#f0fdfa", border: "1px solid #99f6e4",
           borderRadius: 5, padding: "5px 8px", fontWeight: 600, marginBottom: 8 },
  step: { fontSize: 12.5, color: "#374151", lineHeight: 1.55, marginBottom: 5,
          display: "flex", gap: 7 },
  note: { fontSize: 11.5, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a",
          borderRadius: 5, padding: "6px 8px", marginTop: 8, lineHeight: 1.45 },
  grip: { position: "absolute", right: 2, bottom: 2, width: 14, height: 14,
          cursor: "nwse-resize",
          background: "linear-gradient(135deg, transparent 50%, #cbd5e1 50%, #cbd5e1 60%, transparent 60%, transparent 72%, #cbd5e1 72%, #cbd5e1 82%, transparent 82%)" }
};

/* ========================================================================== */
/* The panel                                                                  */
/* ========================================================================== */
export default function Navigator({ currentView, onClose, goTo }) {
  const [pos, setPos] = useState(() => ({
    // Clear of the left rail, near the top. Clamped on first paint below in
    // case the window is small.
    x: Math.min(220, Math.max(8, window.innerWidth - 400)),
    y: 90
  }));
  const [size, setSize] = useState({ w: 380, h: 520 });

  const [query, setQuery] = useState("");
  const [asked, setAsked] = useState(null);      // the question that produced `results`
  const [results, setResults] = useState(null);  // null = nothing asked yet

  const drag = useRef(null);
  const resize = useRef(null);

  const ctx = CONTEXT[currentView] || FALLBACK_CONTEXT;

  /* ---- drag and resize, on window listeners so a fast drag does not
          "fall off" the header ------------------------------------------ */
  useEffect(() => {
    const onMove = (e) => {
      if (drag.current) {
        const nx = e.clientX - drag.current.dx;
        const ny = e.clientY - drag.current.dy;
        // Keep at least a sliver on screen — a panel dragged fully off is
        // unrecoverable without knowing to close and reopen it.
        setPos({
          x: Math.max(-size.w + 80, Math.min(window.innerWidth - 60, nx)),
          y: Math.max(0, Math.min(window.innerHeight - 40, ny))
        });
      } else if (resize.current) {
        setSize({
          w: Math.max(300, Math.min(760, resize.current.w + (e.clientX - resize.current.x))),
          h: Math.max(280, Math.min(window.innerHeight - 40, resize.current.h + (e.clientY - resize.current.y)))
        });
      }
    };
    const onUp = () => { drag.current = null; resize.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [size.w, size.h]);

  const startDrag = (e) => {
    if (e.button !== 0) return;
    drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    e.preventDefault();
  };
  const startResize = (e) => {
    if (e.button !== 0) return;
    resize.current = { x: e.clientX, y: e.clientY, w: size.w, h: size.h };
    e.preventDefault();
    e.stopPropagation();
  };

  /* ---- asking ---------------------------------------------------------- */
  const ask = (q) => {
    const text = String(q ?? query).trim();
    if (!text) return;
    setAsked(text);
    setResults(search(text));
  };

  const openTopic = (id) => {
    const e = HOW_TO.find(h => h.id === id);
    if (!e) return;
    setAsked(e.title);
    setResults([e]);
  };

  // Topics offered when nothing matches — the honest version of "I don't know".
  const suggestions = useMemo(() => {
    const rel = (ctx.related || []).map(id => HOW_TO.find(h => h.id === id)).filter(Boolean);
    if (rel.length >= 3) return rel.slice(0, 5);
    const seen = new Set(rel.map(r => r.id));
    return [...rel, ...HOW_TO.filter(h => !seen.has(h.id)).slice(0, 5 - rel.length)];
  }, [ctx]);

  const Answer = ({ entry }) => (
    <div style={P.card}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: "#111827", marginBottom: 7 }}>
        {entry.title}
      </div>
      <div style={P.where}>{entry.where}</div>
      {entry.steps.map((s, i) => (
        <div key={i} style={P.step}>
          <span style={{ color: "#0f766e", fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span>
          <span>{s}</span>
        </div>
      ))}
      {entry.note && <div style={P.note}>{entry.note}</div>}
      {entry.view && goTo && (
        <button style={P.go} onClick={() => goTo(entry.view)}>
          Take me there →
        </button>
      )}
    </div>
  );

  return (
    <div style={P.panel(pos.x, pos.y, size.w, size.h)} role="dialog" aria-label="Navigator">
      {/* ---- header: drag handle + close ---- */}
      <div style={P.head} onMouseDown={startDrag}>
        <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: 0.2 }}>Navigator</span>
        <button
          onClick={onClose}
          onMouseDown={(e) => e.stopPropagation()}   // closing must not start a drag
          aria-label="Close navigator"
          title="Close"
          style={{ border: "none", background: "rgba(255,255,255,0.15)", color: "#fff",
                   width: 22, height: 22, borderRadius: 4, cursor: "pointer",
                   fontSize: 15, lineHeight: 1, fontFamily: "inherit" }}>
          ×
        </button>
      </div>

      {/* ---- body ---- */}
      <div style={P.body}>
        {/* WHERE YOU ARE — always shown, always current */}
        <div style={P.label}>Where you are</div>
        <div style={{ ...P.card, background: "#f8fafc", borderColor: "#cbd5e1" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0f766e" }}>{ctx.title}</div>
          <div style={{ fontSize: 12.5, color: "#374151", lineHeight: 1.55, marginTop: 4 }}>
            {ctx.blurb}
          </div>
          {!!(ctx.can || []).length && (
            <>
              <div style={{ ...P.label, marginTop: 9, marginBottom: 4 }}>From here you can</div>
              {ctx.can.map((c, i) => (
                <div key={i} style={P.step}>
                  <span style={{ color: "#0f766e", flexShrink: 0 }}>•</span><span>{c}</span>
                </div>
              ))}
            </>
          )}
        </div>

        {/* ANSWER */}
        {results !== null && (
          <>
            <div style={P.label}>
              {results.length ? "Answer" : "No answer for that"}
            </div>
            {asked && (
              <div style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic", marginBottom: 8 }}>
                “{asked}”
              </div>
            )}

            {results.length > 0 && results.map(e => <Answer key={e.id} entry={e} />)}

            {results.length === 0 && (
              <div style={P.card}>
                <div style={{ fontSize: 12.5, color: "#374151", lineHeight: 1.55 }}>
                  I don't have a guide for that one. The Navigator only answers from a written
                  list of tasks, so it never guesses a menu path that doesn't exist — which does
                  mean it has gaps.
                </div>
                <div style={{ ...P.label, marginTop: 10, marginBottom: 3 }}>
                  Try one of these
                </div>
                {suggestions.map(s => (
                  <button key={s.id} style={P.link} onClick={() => openTopic(s.id)}>
                    → {s.title}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* Before anything is asked, offer what is relevant to this screen. */}
        {results === null && (
          <>
            <div style={P.label}>Common tasks from here</div>
            <div style={P.card}>
              {suggestions.map(s => (
                <button key={s.id} style={P.link} onClick={() => openTopic(s.id)}>
                  → {s.title}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ---- ask ---- */}
      <div style={P.foot}>
        <div style={{ ...P.label, marginBottom: 4 }}>Ask a question</div>
        <div style={{ display: "flex", gap: 6 }}>
          <input
            style={P.input}
            value={query}
            placeholder="e.g. How do I create a staff schedule?"
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") ask(); }}
          />
          <button
            onClick={() => ask()}
            style={{ border: "none", background: "#0f766e", color: "#fff", borderRadius: 5,
                     padding: "0 14px", fontSize: 13, fontWeight: 700, cursor: "pointer",
                     fontFamily: "inherit", flexShrink: 0 }}>
            Ask
          </button>
        </div>
        {results !== null && (
          <button
            style={{ ...P.link, marginTop: 6, fontSize: 11.5 }}
            onClick={() => { setResults(null); setAsked(null); setQuery(""); }}>
            ← Clear
          </button>
        )}
      </div>

      <div style={P.grip} onMouseDown={startResize} title="Resize" />
    </div>
  );
}

// Exported so a future maintainer can check coverage, or a test can assert that
// every `view` in HOW_TO is a route App.js actually renders.
export { HOW_TO, CONTEXT };
